import type { ReactElement } from 'react';
import { cn } from '@/lib/utils';

interface RowInputProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  /**
   * Finishes off a half-written value once the user is done with the field — URL rows complete a
   * bare host into a full URL this way, so the form holds, validates and saves the same string the
   * user is looking at.
   */
  complete?: (value: string) => string;
}

export const RowInput = ({
  value,
  onChange,
  placeholder,
  disabled,
  error,
  complete,
}: RowInputProps): ReactElement => {
  // Reports a change only when there was something to complete: a stray tab through an untouched
  // field would otherwise leave the editor believing it holds unsaved edits.
  const commit = () => {
    if (!complete) return;
    const completed = complete(value);
    if (completed !== value) onChange(completed);
  };

  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={commit}
      // Enter finishes the field rather than submitting the whole section: submitting straight from
      // the keyboard is what used to validate a URL before it had been completed.
      onKeyDown={(e) => {
        if (!complete || e.key !== 'Enter') return;
        e.preventDefault();
        commit();
      }}
      placeholder={placeholder}
      disabled={disabled}
      aria-invalid={error}
      className={cn(
        'vtwo:flex-1 vtwo:min-w-0 vtwo:border vtwo:border-gray-200 vtwo:rounded-md vtwo:px-3 vtwo:py-1.5 vtwo:text-sm vtwo:bg-white vtwo:outline-none vtwo:focus:border-blue-400 vtwo:focus:ring-1 vtwo:focus:ring-blue-400/30 vtwo:disabled:opacity-50 vtwo:placeholder:text-gray-400',
        error && 'vtwo:border-red-400'
      )}
    />
  );
};
